import axios from 'axios';

export const extractStatusCode = (error: unknown): number | null => {
  if (!error || typeof error !== 'object') {
    return null;
  }
  const status = (error as { response?: { status?: unknown } }).response?.status;
  return typeof status === 'number' ? status : null;
};

export type CatalogSummary = {
  name: string;
  code: string;
  topImageUrl: string;
};

export type CatalogCategorySummary = {
  name: string;
  code: string;
  imageUrl: string;
};

export type CatalogProductCard = {
  catalogProductId: number;
  title: string;
  imageUrl: string;
};

export type GetCatalogResponse = {
  catalog: CatalogSummary;
  categories: Array<{
    category: CatalogCategorySummary;
    products: CatalogProductCard[];
  }>;
};

export type GetCatalogCategoriesResponse = {
  catalog: CatalogSummary;
  categories: CatalogCategorySummary[];
};

export type GetCatalogCategoryProductsResponse = {
  catalog: CatalogSummary;
  category: CatalogCategorySummary;
  products: CatalogProductCard[];
};

export type GetCatalogProductResponse = {
  catalog: CatalogSummary;
  category: CatalogCategorySummary;
  catalogProduct: CatalogProductDetail;
};

export type CatalogProductDetailOptionUnit = {
  unitId: number;
  name: string;
  imageUrl: string | null;
};

export type CatalogProductDetailOption = {
  optionId: number;
  name: string;
  units: CatalogProductDetailOptionUnit[];
};

export type CatalogProductDetail = {
  catalogProductId: number;
  productId: number;
  title: string;
  description: string;
  images: string[];
  options: CatalogProductDetailOption[];
  includeAlcohol?: boolean;
};

const get = async <T>(path: string): Promise<T> => {
  const response = await axios.get(path, {
    headers: {
      Accept: 'application/json',
    },
  });
  return response.data as T;
};

export const getCatalog = async (catalogCode: string): Promise<GetCatalogResponse> =>
  get<GetCatalogResponse>(`/api/web/catalogs/${encodeURIComponent(catalogCode)}`);

export const getCatalogCategories = async (catalogCode: string): Promise<GetCatalogCategoriesResponse> =>
  get<GetCatalogCategoriesResponse>(`/api/web/catalogs/${encodeURIComponent(catalogCode)}/categories`);

export const getCatalogCategoryProducts = async (
  catalogCode: string,
  categoryCode: string
): Promise<GetCatalogCategoryProductsResponse> =>
  get<GetCatalogCategoryProductsResponse>(
    `/api/web/catalogs/${encodeURIComponent(catalogCode)}/categories/${encodeURIComponent(categoryCode)}`
  );

export const getCatalogProduct = async (
  catalogCode: string,
  catalogProductId: number
): Promise<GetCatalogProductResponse> =>
  get<GetCatalogProductResponse>(`/api/web/catalogs/${encodeURIComponent(catalogCode)}/products/${catalogProductId}`);
